import { FC, useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import styled from '@emotion/styled';
import BasicModal from '__components/Modals/presets/BasicModal';
import TextInput from '__components/Inputs/TextInput';
import LongText from '__components/Inputs/LongText';
import PermissionSettings from '__components/Sharing/PermissionSettings';
import { getSharingLabels } from 'pages/MCPServerSettings/constants';
import { buildSharingSettingsForAPI } from 'utility/sharing';
import type { MCPServerConfigSharingSettings } from '@kizen/api/mcp';

const StyledFieldWrapper = styled.div<{ marginTop?: string }>`
  margin-top: ${({ marginTop }) => marginTop || '15px'};
  text-align: start;
`;

type NewServerModalProps = {
  onConfirm: (
    name: string,
    description: string,
    sharingSettings: Record<string, unknown>
  ) => Promise<void>;
  onHide: () => void;
};

export const NewServerModal: FC<NewServerModalProps> = ({
  onConfirm,
  onHide,
}) => {
  const { t } = useTranslation();
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [permissions, setPermissions] = useState<any>(null);

  const handleConfirm = useCallback(async () => {
    if (!name.trim()) return;
    setSubmitting(true);
    try {
      const sharingSettings =
        (
          buildSharingSettingsForAPI(permissions) as {
            sharing_settings?: MCPServerConfigSharingSettings;
          }
        )?.sharing_settings ?? {};
      await onConfirm(name.trim(), description.trim(), sharingSettings);
    } finally {
      setSubmitting(false);
    }
  }, [name, description, permissions, onConfirm]);

  return (
    <BasicModal
      heading={t('New MCP Server')}
      buttonText={t('Create')}
      defaultLeftBtnText={t('Cancel')}
      disabled={!name.trim() || submitting}
      show
      size="medium"
      onConfirm={handleConfirm}
      onHide={onHide}
    >
      <StyledFieldWrapper marginTop="0">
        <TextInput
          label={t('Name')}
          placeholder={t('Enter server name')}
          value={name}
          onChange={setName}
          required
          data-qa-id="new-mcp-server-name-input"
        />
      </StyledFieldWrapper>
      <StyledFieldWrapper>
        <LongText
          label={t('Description')}
          placeholder={t('Enter description (optional)')}
          value={description}
          onChange={setDescription}
          data-qa-id="new-mcp-server-description-input"
        />
      </StyledFieldWrapper>
      <StyledFieldWrapper>
        <PermissionSettings
          onChange={setPermissions}
          existing={{}}
          removeTopPadding
          includeServiceAccounts
          labels={getSharingLabels(t)}
        />
      </StyledFieldWrapper>
    </BasicModal>
  );
};
